import { defineStore } from 'pinia';
import { useTrackingDataStore } from '@/stores/trackingData.js';
import { configGetter } from '@/config/configGetter.js';

let reconnectTimeout = null;

/**
 * @typedef {Object} WebsocketState
 * @property {boolean} isSubscriptionActive - To check if subscription is active or not
 * @property {boolean} isUpdateAvailable - To check if any update is available
 */
export const useWebsocketStore = defineStore('websocket', {
  /**
   *
   * @returns {WebsocketState}
   */
  state: () => ({
    socket: null,
    isSubscriptionActive: false,
    isUpdateAvailable: false
  }),
  actions: {
    setSubscriptionActive(isActive) {
      this.isSubscriptionActive = isActive;
    },
    setIsUpdateAvailable(isAvailable) {
      this.isUpdateAvailable = isAvailable;
    },

    connectSocket(onopen) {
      const socket = new WebSocket(`${configGetter().WEBSOCKET_URL}`);
      const trackingDataStore = useTrackingDataStore();

      socket.onmessage = ({ data }) => {
        const dataObject = JSON.parse(data);
        switch (dataObject.action) {
          case 'SUBSCRIBED':
            this.isSubscriptionActive = true;
            break;
          case 'NOT_SUBSCRIBED':
            this.isSubscriptionActive = false;
            break;
          case 'DRIVER_LOCATION_CHANGE':
            this.isUpdateAvailable = true;
            if (trackingDataStore.data.enabledCarAnimation) {
              trackingDataStore.updateDriverPosition(dataObject);
            }
            break;
          case 'PARCEL_STATUS_UPDATE':
            this.isUpdateAvailable = true;
            if (trackingDataStore.data.enabledCarAnimation) {
              trackingDataStore.updateParcelStatus(dataObject);
            }
            break;
          default:
        }
      };
      socket.onopen = () => {
        this.socket = socket;
        if (reconnectTimeout !== null) {
          clearTimeout(reconnectTimeout);
          reconnectTimeout = null;
        }
        onopen();
      };
      socket.onclose = () => {
        this.socket = null;
        this.isSubscriptionActive = false;
        if (reconnectTimeout !== null) {
          clearTimeout(reconnectTimeout);
          reconnectTimeout = null;
        }
        reconnectTimeout = setTimeout(
          () => {
            this.connectSocket(onopen);
          },
          this.randomNumberBetween(1000, 3000)
        );
      };
    },
    randomNumberBetween(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },
    subscribeToUpdates(isPrivate) {
      if (this.isSubscriptionActive === false) {
        const trackingDataStore = useTrackingDataStore();
        const request = {
          tenantId: trackingDataStore.data.tenantId,
          jobId: trackingDataStore.data.trackingId
        };
        if (isPrivate) {
          request.action = 'PRIVATE_SUBSCRIBE';
          request.postcode = trackingDataStore.consigneeAddress?.postcode;
        } else {
          request.action = 'PUBLIC_SUBSCRIBE';
        }

        this.socket?.send(JSON.stringify(request));
      }
    },
    initializeUpdates(isPrivate) {
      const trackingDataStore = useTrackingDataStore();

      if (!trackingDataStore.data.enabledWebSocketUpdates) {
        return;
      }
      if (this.socket === null) {
        this.connectSocket(() => this.subscribeToUpdates(isPrivate));
      } else {
        this.subscribeToUpdates(isPrivate);
      }
    },
    unSubscribeToUpdates() {
      this.isSubscriptionActive = false;
    }
  }
});
